/**
 * รายการข้อมูลสินค้า — `GET /v1/products`
 *
 * สามสถานะที่ต้องแยกจากกันเสมอ: กำลังโหลด / โหลดสำเร็จแต่ไม่มีรายการ /
 * โหลดไม่สำเร็จ ตารางว่างที่ใช้แทนทั้งสองอย่างหลังคือหน้าจอที่โกหกผู้ใช้
 *
 * การแบ่งหน้าใช้ `next_cursor` ตาม contract ไม่ใช่ offset — ปุ่ม "โหลดเพิ่ม"
 * จึงโผล่เฉพาะตอนที่เซิร์ฟเวอร์บอกว่ายังมีหน้าถัดไปจริง ๆ
 */

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ProductFailureNotice } from './ProductFailureNotice';
import { detailSearch, productStatusLabel, productStatusTone, orDash } from './productView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { useHeaderOptionItems } from '../desktopContext';
import { exportProducts, fetchProductList, type ProductFailure } from '../../lib/products/client';
import {
  PRODUCT_STATUSES,
  type Product,
  type ProductListFilters,
} from '../../lib/products/types';

export interface ProductListViewProps {
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  /** มาจาก flag `can_create` ที่เซิร์ฟเวอร์ส่งมาเท่านั้น — ดู `ProductMasterScreen` */
  readonly canCreate: boolean;
  readonly createHref: string;
}

type ListState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly items: readonly Product[];
      readonly nextCursor: string | null;
      readonly loadingMore: boolean;
      /** ความล้มเหลวของ "โหลดเพิ่ม" ไม่ควรลบแถวที่โหลดมาแล้วทิ้ง */
      readonly loadMoreFailure: ProductFailure | null;
    }
  | ProductFailure;

function plantCodes(product: Product): string {
  return product.plant_data.map((plant) => plant.plant_code).join(', ');
}

export function ProductListView({
  apiBaseUrl,
  fetchImpl,
  canCreate,
  createHref,
}: ProductListViewProps) {
  const { pathname } = useLocation();
  const [filters, setFilters] = useState<ProductListFilters>({});
  const [queryDraft, setQueryDraft] = useState('');
  const [statusDraft, setStatusDraft] = useState('');
  const [modelDraft, setModelDraft] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<ListState>({ status: 'loading' });

  const [exportPending, setExportPending] = useState(false);
  const [exportFailure, setExportFailure] = useState<ProductFailure | null>(null);
  const setHeaderOptionItems = useHeaderOptionItems();
  /** ตัวกรองที่ใช้อยู่จริง (หลังกด "กรองรายการ") ไม่ใช่ draft — `onSelect` อ่านค่านี้ตอนกด ไม่ใช่ตอนลงทะเบียน */
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const exportPendingRef = useRef(false);

  const runExport = useCallback(() => {
    if (exportPendingRef.current) {
      return;
    }
    exportPendingRef.current = true;
    setExportPending(true);
    setExportFailure(null);
    void exportProducts(filtersRef.current, { apiBaseUrl, fetchImpl }).then((result) => {
      exportPendingRef.current = false;
      setExportPending(false);
      if (result.status !== 'ready' && result.status !== 'cancelled') {
        setExportFailure(result);
      }
    });
  }, [apiBaseUrl, fetchImpl]);

  useEffect(() => {
    setHeaderOptionItems([
      {
        id: 'products-export',
        label: exportPending ? 'กำลังส่งออก…' : 'Export Excel',
        onSelect: runExport,
        disabled: exportPending,
        testId: 'products-export',
      },
    ]);
    return () => {
      setHeaderOptionItems([]);
    };
  }, [exportPending, runExport, setHeaderOptionItems]);

  useEffect(() => {
    setExportFailure(null);
  }, [filters]);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setState({ status: 'loading' });

    void fetchProductList(filters, {
      apiBaseUrl,
      fetchImpl,
      signal: controller.signal,
    }).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setState(
        result.status === 'ready'
          ? {
              status: 'ready',
              items: result.page.items,
              nextCursor: result.page.next_cursor,
              loadingMore: false,
              loadMoreFailure: null,
            }
          : result,
      );
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [filters, apiBaseUrl, fetchImpl, reloadToken]);

  const applyFilters = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFilters({ q: queryDraft, status: statusDraft, model: modelDraft });
  };

  const loadMore = async () => {
    if (state.status !== 'ready' || state.loadingMore) {
      return;
    }
    const loaded = state;
    const cursor = loaded.nextCursor;
    if (cursor === null) {
      return;
    }
    setState({ ...loaded, loadingMore: true, loadMoreFailure: null });

    const result = await fetchProductList({ ...filters, cursor }, { apiBaseUrl, fetchImpl });
    if (result.status === 'cancelled') {
      return;
    }
    setState(
      result.status === 'ready'
        ? {
            status: 'ready',
            items: [...loaded.items, ...result.page.items],
            nextCursor: result.page.next_cursor,
            loadingMore: false,
            loadMoreFailure: null,
          }
        : { ...loaded, loadingMore: false, loadMoreFailure: result },
    );
  };

  return (
    <div className="cap-page" data-testid="product-list">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            ข้อมูลสินค้า (Product Master)
            {/*
              `WO-P2-M11-004` (D-107 ข้อ 5) — ประโยคขอบเขตโรงงานย้ายออกจากตัวหน้า
              (เคยเป็น `<p className="cap-page__sub">` อยู่ตรงนี้) เข้าไปอยู่ใน (i)
              แทน **ย้าย ไม่ใช่ลบ** — ยังจำเป็นตอนผู้ใช้สงสัยว่าทำไมของหาย เพียงแต่
              ไม่ต้องกินที่ทุกวินาทีที่เปิดหน้า
            */}
            <InfoHint
              label="เกี่ยวกับขอบเขตโรงงานที่แสดงในรายการนี้"
              text="รายการถูกจำกัดด้วยโรงงานที่คุณมีสิทธิ์เห็นเสมอ — สินค้าที่ไม่มีข้อมูลในโรงงานเหล่านั้นจะไม่ปรากฏที่นี่"
            />
          </h1>
        </div>
        <div className="cap-page__actions">
          {canCreate ? (
            <Link
              className="cap-button cap-button--primary"
              data-testid="product-create-link"
              to={{ pathname, search: createHref }}
            >
              สร้างข้อมูลสินค้าใหม่
            </Link>
          ) : null}
        </div>
      </div>

      <form className="cap-filters" onSubmit={applyFilters} aria-label="ตัวกรองรายการสินค้า">
        <label className="cap-field">
          <span className="cap-field__label">ค้นหา รหัส/ชื่อ</span>
          <input
            className="cap-input"
            type="search"
            name="q"
            value={queryDraft}
            onChange={(event) => {
              setQueryDraft(event.target.value);
            }}
            placeholder="เช่น RM-STEEL-8MM"
          />
        </label>
        <label className="cap-field">
          <span className="cap-field__label">
            Model{' '}
            <InfoHint
              label="เกี่ยวกับตัวกรอง Model"
              text="กรองด้วยข้อความบางส่วนของ Model (ไม่สนตัวพิมพ์ใหญ่เล็ก) แยกจากช่องค้นหารหัส/ชื่อ"
            />
          </span>
          <input
            className="cap-input"
            type="text"
            name="model"
            data-testid="product-list-model-filter"
            value={modelDraft}
            onChange={(event) => {
              setModelDraft(event.target.value);
            }}
            placeholder="เช่น RX-100"
          />
        </label>
        <label className="cap-field">
          <span className="cap-field__label">สถานะ</span>
          <select
            className="cap-input"
            name="status"
            value={statusDraft}
            onChange={(event) => {
              setStatusDraft(event.target.value);
            }}
          >
            <option value="">ทุกสถานะ</option>
            {PRODUCT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {productStatusLabel(status)}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="cap-button cap-button--ghost">
          กรองรายการ
        </button>
      </form>

      {exportFailure === null ? null : (
        <ProductFailureNotice failure={exportFailure} context="list" />
      )}

      {state.status === 'loading' ? (
        <div className="cap-notice" data-testid="product-list-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการสินค้า…</p>
        </div>
      ) : null}

      {state.status !== 'loading' && state.status !== 'ready' ? (
        <ProductFailureNotice
          failure={state}
          context="list"
          onRetry={() => {
            setReloadToken((token) => token + 1);
          }}
        />
      ) : null}

      {state.status === 'ready' && state.items.length === 0 ? (
        <div className="cap-notice" data-testid="product-list-empty" role="status">
          <StatusBadge tone="neutral">ไม่มีรายการ</StatusBadge>
          <h2 className="cap-notice__title">ไม่พบข้อมูลสินค้าตามเงื่อนไขนี้</h2>
          <p className="cap-notice__text">
            เซิร์ฟเวอร์ตอบสำเร็จแต่ไม่มีรายการที่ตรงเงื่อนไขและอยู่ในโรงงานที่คุณมีสิทธิ์เห็น —
            ไม่ใช่การโหลดที่ล้มเหลว
          </p>
        </div>
      ) : null}

      {state.status === 'ready' && state.items.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="รายการข้อมูลสินค้า">
          <div className="cap-card__head">
            <h2 className="cap-card__title">รายการข้อมูลสินค้า</h2>
            <span className="cap-card__hint">แสดง {state.items.length} รายการที่โหลดมาแล้ว</span>
          </div>
          <div className="cap-table-wrap">
            <table className="cap-table">
              <caption className="cap-visually-hidden">
                รายการข้อมูลสินค้าที่คุณมีสิทธิ์เห็น พร้อมสถานะและโรงงานที่เกี่ยวข้อง
              </caption>
              <thead>
                <tr>
                  <th scope="col">รหัสสินค้า</th>
                  <th scope="col">ชื่อ</th>
                  <th scope="col">รุ่น/โมเดล</th>
                  <th scope="col">ประเภท</th>
                  <th scope="col">หน่วยนับ</th>
                  <th scope="col">สถานะ</th>
                  <th scope="col">โรงงานที่คุณเห็น</th>
                  <th scope="col">แก้ไขล่าสุด</th>
                </tr>
              </thead>
              <tbody>
                {state.items.map((product) => (
                  <tr key={product.product_id} data-testid={`product-row-${product.product_code}`}>
                    <th scope="row" className="cap-table__row-head">
                      <Link
                        className="cap-table__link"
                        to={{ pathname, search: detailSearch(product.product_id) }}
                      >
                        {product.product_code}
                      </Link>
                    </th>
                    <td>{product.name}</td>
                    <td className="cap-table__muted" data-testid={`product-model-${product.product_code}`}>
                      {orDash(product.model)}
                    </td>
                    <td className="cap-table__code">{product.product_type}</td>
                    <td className="cap-table__code">{product.base_uom}</td>
                    <td>
                      <StatusBadge tone={productStatusTone(product.status)}>
                        {productStatusLabel(product.status)}
                      </StatusBadge>
                    </td>
                    <td className="cap-table__muted">{orDash(plantCodes(product))}</td>
                    <td className="cap-table__code">{product.updated_at}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cap-card__foot">
            {state.nextCursor === null ? (
              <span data-testid="product-list-complete">
                แสดงครบทุกรายการที่ตรงเงื่อนไขแล้ว
              </span>
            ) : (
              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid="product-list-more"
                onClick={() => {
                  void loadMore();
                }}
                disabled={state.loadingMore}
              >
                {state.loadingMore ? 'กำลังโหลดเพิ่ม…' : 'โหลดรายการถัดไป'}
              </button>
            )}
          </div>
        </section>
      ) : null}

      {state.status === 'ready' && state.loadMoreFailure !== null ? (
        <ProductFailureNotice failure={state.loadMoreFailure} context="list" />
      ) : null}
    </div>
  );
}
